<script setup lang="ts">
import { computed } from "vue";

interface BlogPostMeta {
  slug: string;
  title: string;
  author?: string;
  publishedAt?: string;
  excerpt?: string;
  readTime?: string;
}

const { data: posts } = await useFetch<BlogPostMeta[]>("/api/blog");
const normalizedPosts = computed(() =>
  (posts.value ?? []).map((post) => ({
    ...post,
    path: `/blog/${post.slug}`,
  })),
);

useSeoMeta({
  title: "Ramzan 2026 Blog Pakistan | Sehri, Iftar, Roza Guides",
  description:
    "Roman Urdu Ramadan guides for Pakistan: aaj kitna roza hai, sehri mein kya khayein, iftar time today, aur practical Ramzan 2026 tips.",
  ogTitle: "Ramzan 2026 Blog Pakistan | Rozay",
  ogDescription:
    "Roman Urdu Ramadan guides for Pakistan: sehri, iftar, roza countdown, aur Ramzan 2026 practical tips.",
  ogUrl: "https://rozay.today/blog/",
  ogType: "website",
  ogImage: "https://rozay.today/blog/ramadan-2026-guide.svg",
  twitterCard: "summary_large_image",
  twitterImage: "https://rozay.today/blog/ramadan-2026-guide.svg",
  keywords:
    "ramzan 2026 pakistan, aaj kitna roza hai, sehri time today, iftar time today, roza countdown, sehri mein kya khayein",
});

const itemListSchema = computed(() => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: normalizedPosts.value.map((post, index) => ({
    "@type": "ListItem",
    position: index + 1,
    url: `https://rozay.today${post.path}/`,
    name: post.title,
  })),
}));

useHead(() => ({
  link: [{ rel: "canonical", href: "https://rozay.today/blog/" }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(itemListSchema.value),
    },
  ],
}));
</script>

<template>
  <section class="max-w-3xl mx-auto px-4 py-8" aria-labelledby="blog-page-title">
    <h1 id="blog-page-title" class="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100">
      Ramzan Blog (Roman Urdu)
    </h1>
    <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
      Ramzan 2026 ke practical guides: aaj kitna roza hai, Sehri/Iftar timing aur daily routine tips.
    </p>

    <ul class="mt-6 space-y-4">
      <li
        v-for="post in normalizedPosts"
        :key="post.slug"
        class="rounded-xl border border-gray-200 dark:border-gray-800 p-4"
      >
        <NuxtLink
          :to="post.path"
          class="text-lg font-medium text-gray-900 dark:text-gray-100 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 rounded-sm"
        >
          {{ post.title }}
        </NuxtLink>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {{ post.publishedAt }}<span v-if="post.readTime"> | {{ post.readTime }}</span>
        </p>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
          {{ post.excerpt }}
        </p>
      </li>
    </ul>
  </section>
</template>
